/*!
 * Kwolly — Drawer Animation Override v2.0  "Spring Slide"
 *
 * iOS-style spring physics drawer animation — multi-keyframe approach
 * that simulates spring oscillation without any library. The panel
 * shoots in from below, overshoots slightly, then settles naturally.
 *
 * Strategy: monkey-patches Drawer.prototype._showTransition and
 * _hideTransition via customElements.whenDefined(), so all drawer
 * variants (cart, search, navigation, etc.) inherit the new motion
 * without touching theme.js.
 *
 * Spring enter keyframes (on content panel):
 *   0 %  → translateY(60%)          — starts off-screen below
 *   62%  → translateY(-2.5%)        — overshoots past zero (spring tension)
 *   80%  → translateY(1%)           — bounces back above zero
 *   91%  → translateY(-0.4%)        — micro oscillation
 *   100% → translateY(0)            — settles at rest
 *   Opacity fades in during first 62% so the "blur of motion" is natural.
 *
 * Exit: decisive ease-in slide-down — springs shouldn't hesitate on exit.
 *
 * Timing:
 *   Host fade-in     120 ms  ease-out
 *   Panel spring     500 ms  ease-out  (enter)
 *   Content stagger   60 ms  per child, starting 160 ms after panel
 *   Panel exit       280 ms  ease-in   (exit)
 *   Total enter      ~760 ms perceived (spring settle)
 *   Total exit       ~430 ms perceived
 *
 * v2.0 — Spring Slide replaces Soft Reveal (v1.1).
 *        First-open flash fix retained (synchronous pre-set).
 */

(function kwollyDrawerAnimations() {
  'use strict';

  // Respect OS-level and theme-level reduced-motion preferences
  const REDUCE =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    Boolean(window.themeVariables?.settings?.reduceDrawerAnimation);

  // ── Easing tokens ─────────────────────────────────────────────
  // Spring enter: ease-out is the global envelope — the spring
  // oscillation comes from multi-keyframe offsets, not from easing.
  const EASE_ENTER = 'ease-out';
  const EASE_EXIT  = 'cubic-bezier(0.5, 0, 0.75, 0)'; // decisive ease-in

  // ── Spring keyframes ───────────────────────────────────────────
  // Simulates iOS-style spring physics via multi-offset keyframes.
  // Opacity reaches 1 at the first overshoot (offset 0.62) so the
  // initial blur-of-motion feels natural, not like a slow fade.
  const SPRING_ENTER = [
    { transform: 'translateY(60%)',    opacity: '0', offset: 0    },
    { transform: 'translateY(-2.5%)', opacity: '1', offset: 0.62 },
    { transform: 'translateY(1%)',    opacity: '1', offset: 0.80 },
    { transform: 'translateY(-0.4%)', opacity: '1', offset: 0.91 },
    { transform: 'translateY(0)',     opacity: '1', offset: 1    },
  ];

  // ── Core animation helper ──────────────────────────────────────
  // Returns a Promise that resolves when the Web Animation finishes.
  function run(el, keyframes, options) {
    const anim = el.animate(keyframes, {
      fill: 'forwards',
      composite: 'replace',
      ...options,
    });
    return anim.finished;
  }

  // Stagger a NodeList — each child delayed by `staggerMs` more than previous.
  function runStagger(els, keyframes, options, staggerMs) {
    const base = options.delay || 0;
    return Promise.all(
      Array.from(els).map((el, i) =>
        run(el, keyframes, { ...options, delay: base + i * staggerMs })
      )
    );
  }

  // ── Show (open) transition ─────────────────────────────────────
  async function showTransition(doAnimate = true) {
    // Position the drawer on screen (sets left/right/bottom CSS)
    this._setInitialPosition();

    // Collect nav link items for stagger (navigation-drawer only)
    const isNavDrawer = this.tagName.toLowerCase() === 'navigation-drawer';
    const navItems = isNavDrawer
      ? Array.from(
          this.querySelector('.panel-list__wrapper > .panel:first-child .panel__scroller > ul:first-child')
            ?.querySelectorAll(':scope > li') ?? []
        )
      : [];

    // Reduced-motion: plain opacity fade, no spatial movement
    if (REDUCE || !doAnimate) {
      navItems.forEach((li) => { li.style.opacity = ''; li.style.transform = ''; });
      const a = this.animate(
        [
          { opacity: '0', visibility: 'hidden' },
          { opacity: '1', visibility: 'visible' },
        ],
        { duration: doAnimate ? 200 : 0, fill: 'forwards' }
      );
      if (!doAnimate) a.finish();
      await a.finished;
      this.classList.add('show-close-cursor');
      return;
    }

    const content     = this.shadowRoot?.querySelector('[part="content"]');
    const closeButton = this.shadowRoot?.querySelector('[part="outside-close-button"]');

    // ── Pre-set initial states SYNCHRONOUSLY ──────────────────────
    // Prevents first-open flash: inner elements must be invisible before
    // the host starts fading in. Browser won't paint between sync statements.
    if (content) {
      content.style.opacity   = '0';
      content.style.transform = 'translateY(60%)';
      Array.from(content.children).forEach((ch) => { ch.style.opacity = '0'; });
    }
    if (closeButton) closeButton.style.opacity = '0';

    // Pre-set nav items for stagger
    navItems.forEach((li) => {
      li.style.opacity   = '0';
      li.style.transform = 'translateY(10px)';
    });

    // Force layout acknowledgement before handing back to microtask queue.
    void this.offsetHeight;

    // ── Run all animations in parallel ────────────────────────────

    // 1 — Host fades in quickly (reveals the overlay backdrop)
    const hostPromise = run(
      this,
      [
        { opacity: '0', visibility: 'hidden'  },
        { opacity: '1', visibility: 'visible' },
      ],
      { duration: 120, easing: 'ease-out' }
    );

    if (!content) {
      await hostPromise;
      this.classList.add('show-close-cursor');
      return;
    }

    // 2 — Panel spring: shoots up, overshoots, oscillates, settles
    const panelPromise = run(content, SPRING_ENTER, {
      duration: 500,
      easing: EASE_ENTER,
    });

    // 3 — Content children (header / body / footer) stagger in after
    //     the spring has reached its first overshoot (~310 ms in).
    //     They materialise as the panel is "arriving", not before.
    const childrenPromise = runStagger(
      content.children,
      [{ opacity: '0' }, { opacity: '1' }],
      { duration: 200, easing: 'ease-out', delay: 160 },
      60  // 60 ms between header → body → footer
    );

    // 4 — Outside close button appears with content
    const closePromise = closeButton
      ? run(
          closeButton,
          [{ opacity: '0' }, { opacity: '1' }],
          { duration: 180, easing: 'ease-out', delay: 200 }
        )
      : Promise.resolve();

    // 5 — Gradient depth reveal (navigation drawer only)
    //     --kwolly-nav-cool animates from flat cream to soft mint,
    //     revealing depth as the panel settles. Requires @property
    //     registration in kwolly-drawers.css. Falls back gracefully
    //     in Firefox (no @property support = static gradient).
    if (isNavDrawer && content && !REDUCE) {
      run(
        content,
        [
          { '--kwolly-nav-cool': '#f7f4f2' },
          { '--kwolly-nav-cool': '#cce8df' },
        ],
        { duration: 900, easing: 'ease-out', delay: 280, fill: 'forwards' }
      );
    }

    // 6 — Nav links stagger in one by one (navigation drawer only)
    //     Starts after panel body has settled (~240 ms), each item 45 ms apart.
    const navStaggerPromise = navItems.length
      ? Promise.all(
          navItems.map((li, i) =>
            run(
              li,
              [
                { opacity: '0', transform: 'translateY(10px)' },
                { opacity: '1', transform: 'translateY(0)'    },
              ],
              { duration: 220, easing: 'ease-out', delay: 240 + i * 45 }
            )
          )
        )
      : Promise.resolve();

    await Promise.all([hostPromise, panelPromise, childrenPromise, closePromise, navStaggerPromise]);
    this.classList.add('show-close-cursor');
  }

  // ── Hide (close) transition ────────────────────────────────────
  async function hideTransition() {
    // Reset nav item inline styles + gradient so they replay on next open
    if (this.tagName.toLowerCase() === 'navigation-drawer') {
      const navItems = this.querySelectorAll(
        '.panel-list__wrapper > .panel:first-child .panel__scroller > ul:first-child > li'
      );
      navItems.forEach((li) => { li.style.opacity = ''; li.style.transform = ''; });

      // Reset gradient depth so reveal replays next open
      const contentEl = this.shadowRoot?.querySelector('[part="content"]');
      if (contentEl) {
        contentEl.animate(
          [{ '--kwolly-nav-cool': '#f7f4f2' }],
          { duration: 0, fill: 'forwards' }
        );
      }
    }

    // Reduced-motion: plain opacity fade
    if (REDUCE) {
      return this.animate(
        [
          { opacity: '1', visibility: 'visible' },
          { opacity: '0', visibility: 'hidden'  },
        ],
        { duration: 200, fill: 'forwards' }
      ).finished;
    }

    const content     = this.shadowRoot?.querySelector('[part="content"]');
    const closeButton = this.shadowRoot?.querySelector('[part="outside-close-button"]');

    this.classList.remove('show-close-cursor');

    const promises = [];

    // 1 — Outside close button disappears immediately
    if (closeButton) {
      promises.push(
        run(closeButton, [{ opacity: '1' }, { opacity: '0' }], {
          duration: 100,
          easing: 'ease-in',
        })
      );
    }

    if (content) {
      // 2 — Content children fade out quickly (all at once, no stagger)
      promises.push(
        runStagger(
          content.children,
          [{ opacity: '1' }, { opacity: '0' }],
          { duration: 100, easing: 'ease-in' },
          0
        )
      );

      // 3 — Panel slides decisively down and out — no spring on exit,
      //     springs should feel purposeful and not hesitate when closing.
      promises.push(
        run(
          content,
          [
            { transform: 'translateY(0)',   opacity: '1' },
            { transform: 'translateY(40%)', opacity: '0' },
          ],
          { duration: 280, easing: EASE_EXIT, delay: 60 }
        )
      );
    }

    // 4 — Host fades out last (carries the overlay)
    promises.push(
      run(
        this,
        [
          { opacity: '1', visibility: 'visible' },
          { opacity: '0', visibility: 'hidden'  },
        ],
        { duration: 200, easing: 'ease-in', delay: 240 }
      )
    );

    return Promise.all(promises);
  }

  // ── Patch all drawer variants ──────────────────────────────────
  // We target x-drawer (the base class) plus every concrete subclass.
  // Subclasses that don't override _showTransition/_hideTransition
  // will automatically use our new implementation via prototype chain.
  // Subclasses that DO override them (none currently in Impact) would
  // need separate entries here.
  const DRAWER_TAGS = [
    'x-drawer',
    'cart-drawer',
    'search-drawer',
    'navigation-drawer',
    'facet-drawer',
    'quick-buy-drawer',
    'cart-notification-drawer',
  ];

  DRAWER_TAGS.forEach((tag) => {
    customElements.whenDefined(tag).then(() => {
      const Cls = customElements.get(tag);
      if (!Cls) return;
      Cls.prototype._showTransition = showTransition;
      Cls.prototype._hideTransition = hideTransition;
    });
  });
})();
